/**
 * useSSEConnection hook
 *
 * Manages Server-Sent Events (SSE) connection for real-time DTMF events with authenticated SSE.
 */

import { useEffect, useRef } from 'react';
import type { CardType, DTMFEvent } from '@/types/voice-dtmf.types';
import type { AgentApiClient } from '@services/agent/AgentApiClient';
import { detectCardType } from '@utils/cardUtils';
import { logger } from '@utils/logger';

const mapCardScheme = (scheme: string): CardType | undefined => {
  switch (scheme.toUpperCase()) {
    case 'VISA':
      return 'VISA';
    case 'MASTERCARD':
    case 'MASTER':
      return 'MASTERCARD';
    case 'AMEX':
    case 'AMERICAN EXPRESS':
      return 'AMEX';
    default:
      return undefined;
  }
};

interface UseSSEConnectionProps {
  // Pass only the terminal flag, not the full state object — avoids tearing down
  // the SSE connection on every screen transition (sessionState object identity
  // changes on each setSessionState call, which would re-run the effect and
  // disconnect/reconnect the SSE on every card-entry step).
  isTerminalScreen: boolean;
  sseUrl?: string;
  useMockData?: boolean;
  onDTMFEvent: (event: DTMFEvent) => void;
  onConnectionError?: () => void;
  apiClient?: AgentApiClient | null; // Optional API client for authenticated SSE
}

/**
 * Hook to manage SSE connection for real-time DTMF events
 *
 * Note: Currently SSE is not fully implemented. This hook establishes
 * the connection but event handling needs to be completed based on
 * backend SSE event format.
 */
export const useSSEConnection = ({
  isTerminalScreen,
  sseUrl,
  useMockData = false,
  onDTMFEvent,
  onConnectionError,
  apiClient,
}: UseSSEConnectionProps) => {
  const eventSourceRef = useRef<EventSource | null>(null);
  const sseSubscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);

  useEffect(() => {
    // Skip SSE in mock mode or if session has reached a terminal state.
    // isTerminalScreen is derived outside this hook so that normal screen
    // transitions (e.g. card-entry → processing) do NOT re-run this effect
    // and tear down the connection mid-session.
    if (useMockData || isTerminalScreen) {
      return;
    }

    // Use authenticated SSE connection if apiClient is available
    if (apiClient) {
      logger.info('Creating authenticated SSE connection');

      try {
        const endpoint = sseUrl || `/api/events/stream`;

        const subscription = apiClient.createSSEConnection({
          endpoint,
          handlers: {
            onFieldProgress: (data) => {
              // Map backend FIELD_PROGRESS payload to the shape the component reads
              const field =
                data.elementName === 'PAN'
                  ? 'CARD_NUMBER'
                  : data.elementName === 'CVC'
                    ? 'CVC'
                    : null;
              // EXPIRY has no structured field in cardEntry — skip
              if (!field) return;
              const cardType =
                field === 'CARD_NUMBER'
                  ? data.cardScheme
                    ? mapCardScheme(data.cardScheme)
                    : detectCardType(data.maskedData)
                  : undefined;
              const dtmfEvent: DTMFEvent = {
                eventType: 'FIELD_COMPLETE',
                timestamp: new Date().toISOString(),
                data: {
                  field,
                  maskedValue: data.maskedData,
                  digitsCollected: data.length,
                  totalDigits: data.totalRequired,
                  // FIELD_PROGRESS must never mark a field as complete — only
                  // FIELD_COLLECTED(validationState=VALID) has that authority.
                  // Setting isComplete: true here creates a race: if FIELD_PROGRESS(100%)
                  // arrives after FIELD_COLLECTED(INVALID) has already cleared the field,
                  // it re-marks it as valid, hiding the validation failure from the agent.
                  isComplete: false,
                  ...(cardType ? { cardType } : {}),
                },
              };
              onDTMFEvent(dtmfEvent);
            },
            onFieldCollected: (data) => {
              // FIELD_COLLECTED confirms entry is done but carries no maskedData.
              // Emit FIELD_COMPLETE only when validation passed; FIELD_INVALID otherwise
              // so the component can clear the field that Sycurio rejected.
              const field =
                data.elementName === 'PAN'
                  ? 'CARD_NUMBER'
                  : data.elementName === 'CVC'
                    ? 'CVC'
                    : null;
              if (!field) return; // EXPIRY — no structured cardEntry field
              if (data.validationState !== 'VALID') {
                onDTMFEvent({
                  eventType: 'FIELD_INVALID',
                  timestamp: data.timestamp,
                  data: { field },
                });
                return;
              }
              onDTMFEvent({
                eventType: 'FIELD_COMPLETE',
                timestamp: data.timestamp,
                data: { field, isComplete: true },
              });
            },
            onFieldValidationFailed: (data) => {
              // Defensive: FIELD_VALIDATION_FAILED always arrives after FIELD_COLLECTED INVALID.
              // Emitting FIELD_INVALID again is harmless — the field is already cleared —
              // but guards against any race where FIELD_COLLECTED was missed.
              const field =
                data.elementName === 'PAN'
                  ? 'CARD_NUMBER'
                  : data.elementName === 'CVC'
                    ? 'CVC'
                    : null;
              if (!field) return; // EXPIRY invalid is non-critical, no reset needed
              onDTMFEvent({
                eventType: 'FIELD_INVALID',
                timestamp: new Date().toISOString(),
                data: { field },
              });
            },
            onVoiceManualRecommended: () => {
              onDTMFEvent({
                eventType: 'VOICE_MANUAL_RECOMMENDED',
                timestamp: new Date().toISOString(),
              });
            },
            onProgress: (data) => {
              const dtmfEvent: DTMFEvent = {
                eventType: 'PAYMENT_PROCESSING',
                timestamp: new Date().toISOString(),
                data,
              };
              onDTMFEvent(dtmfEvent);
            },
            onPaymentComplete: (data) => {
              const dtmfEvent: DTMFEvent = {
                eventType: 'PAYMENT_COMPLETE',
                timestamp: new Date().toISOString(),
                data,
              };
              onDTMFEvent(dtmfEvent);
            },
            onPaymentFailed: (data) => {
              const dtmfEvent: DTMFEvent = {
                eventType: 'PAYMENT_DECLINED',
                timestamp: new Date().toISOString(),
                data,
              };
              onDTMFEvent(dtmfEvent);
            },
            onSecureModeEntered: (data) => {
              const dtmfEvent: DTMFEvent = {
                eventType: 'SECURE_MODE_ENTERED',
                timestamp: new Date().toISOString(),
                data,
              };
              onDTMFEvent(dtmfEvent);
            },
            onSessionUpdated: (data) => {
              const dtmfEvent: DTMFEvent = {
                eventType: 'SESSION_UPDATED',
                timestamp: new Date().toISOString(),
                data,
              };
              onDTMFEvent(dtmfEvent);
            },
            onSessionFailed: (data) => {
              const dtmfEvent: DTMFEvent = {
                eventType: 'SESSION_FAILED',
                timestamp: new Date().toISOString(),
                data,
              };
              onDTMFEvent(dtmfEvent);
            },
            onVoiceManualInitiated: (data) => {
              const dtmfEvent: DTMFEvent = {
                eventType: 'VOICE_MANUAL_INITIATED',
                timestamp: new Date().toISOString(),
                data,
              };
              onDTMFEvent(dtmfEvent);
            },
            onSessionExpired: () => {
              // Backend-authoritative expiry: expire immediately rather than
              // waiting for the client-side TTL timer (which remains as a fallback).
              onDTMFEvent({
                eventType: 'SESSION_EXPIRED',
                timestamp: new Date().toISOString(),
              });
            },
            onError: (error) => {
              logger.error('SSE error', error);
              onConnectionError?.();
            },
          },
          onReconnectFailed: () => {
            logger.error('SSE reconnection exhausted');
            onConnectionError?.();
          },
        });

        sseSubscriptionRef.current = subscription;

        return () => {
          logger.info('Cleaning up authenticated SSE connection');
          subscription.unsubscribe();
        };
      } catch (error) {
        logger.error('Failed to create authenticated SSE connection', error);
        return;
      }
    }

    // Fallback: Use legacy EventSource (for backward compatibility)
    const endpoint = sseUrl || `/api/events/stream`;
    const eventSource = new EventSource(endpoint);
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => logger.info('SSE Connected');

    eventSource.addEventListener('DTMF_EVENT', (event) => {
      try {
        const dtmfEvent: DTMFEvent = JSON.parse((event as MessageEvent).data);
        logger.info('DTMF Event received', dtmfEvent);
        onDTMFEvent(dtmfEvent);
      } catch (err) {
        logger.error('SSE Parse error', err);
      }
    });

    eventSource.onerror = () => {
      logger.warn('SSE Connection error');
      eventSource.close();
      onConnectionError?.();
    };

    return () => {
      if (eventSourceRef.current) {
        eventSource.close();
        eventSourceRef.current = null;
      }
    };
  }, [
    isTerminalScreen,
    sseUrl,
    useMockData,
    onDTMFEvent,
    onConnectionError,
    apiClient,
  ]);

  return {
    eventSourceRef,
  };
};
